import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

export default function SeedEditorial() {
    return (
        <section
            id="philosophy"
            data-testid="seed-editorial-section"
            className="relative py-28 lg:py-40 bg-bone overflow-hidden"
        >
            {/* Soft botanical/organic wash — kept as a whisper, not a literal motif */}
            <div
                aria-hidden
                className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full opacity-[0.06] blur-3xl"
                style={{ background: "#4A7C2A" }}
            />

            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                    (Philosophy / 02)
                </div>

                <div className="grid grid-cols-12 gap-y-12 lg:gap-x-12 mt-10">
                    {/* Editorial headline */}
                    <div className="col-span-12 lg:col-span-9">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease }}
                            data-testid="seed-editorial-heading"
                            className="font-serif italic font-medium tracking-[-0.01em] leading-[0.98] text-5xl sm:text-6xl lg:text-[8.2vw]"
                        >
                            Every great brand
                            <br />
                            starts with a{" "}
                            <span className="text-olive not-italic font-display font-black uppercase tracking-tight">
                                seed.
                            </span>
                        </motion.h2>
                    </div>

                    {/* Right sub copy */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease, delay: 0.2 }}
                        className="col-span-12 lg:col-span-3 lg:pt-10"
                    >
                        <div className="text-xs uppercase tracking-[0.2em] font-bold text-black/55 mb-3">
                            — A note from the studio
                        </div>
                        <p className="font-sans text-base lg:text-lg text-black/70 leading-relaxed max-w-sm">
                            Beej means seed. Every brand we&apos;ve helped build
                            began as one — a stubborn idea, a half-finished
                            sketch, a founder who refused to look like anyone
                            else.
                        </p>
                    </motion.div>
                </div>

                {/* Body editorial */}
                <div className="mt-20 lg:mt-28 grid grid-cols-12 gap-y-10 lg:gap-x-12">
                    <div className="col-span-12 lg:col-span-7 lg:col-start-3">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease }}
                            className="space-y-7 text-lg lg:text-xl text-black/80 leading-[1.65]"
                        >
                            <p>
                                A seed doesn&apos;t care how the soil looks. It
                                cares whether the soil is real. Most brands
                                fail not because the idea was weak, but because
                                the system around it was performative —
                                identity built for awards, content built for
                                feeds, growth built on rented attention.
                            </p>
                            <p className="font-serif italic text-2xl lg:text-3xl text-black/90 leading-[1.35]">
                                We build the soil. The light. The water. The
                                patience.
                            </p>
                            <p>
                                Digitalbeej is small on purpose. Senior on
                                purpose. We work with a handful of founders at
                                a time — not to look exclusive, but because
                                real growth needs proximity. A boutique studio
                                with the rigour of a strategy firm and the
                                taste of an editorial design house.
                            </p>
                            <p>
                                From the first sketch to the hundredth
                                campaign, our job stays the same: help your
                                brand grow into something the market can&apos;t
                                ignore — without losing what made the seed
                                worth planting in the first place.
                            </p>
                        </motion.div>

                        {/* Signature */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease, delay: 0.3 }}
                            className="mt-14 flex items-center gap-5"
                        >
                            <div className="w-12 h-px bg-black" />
                            <div>
                                <div className="font-serif italic text-2xl text-black/85">
                                    The Digitalbeej Studio
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/55 mt-1">
                                    Mumbai · est. boutique
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
